import type { ReactNode } from "react";
import { format, formatISO, isValid } from "date-fns";
import classNames from "classnames";
import { fullDateFormatWithTime } from "@common/helpers/date";
import { ActorIcon } from "@components/history/components/ActorIcon";
import { ActorName } from "../components/ActorName";
import { useVisibleHistoryChannel } from "../hooks/useVisibleHistoryChannel";
import { HistoryRow } from "../views/historyViewConfig";
import {
    resolveActorChannelLabel,
    resolveActorDisplayName,
} from "../utils/actorUtils";
import styles from "./ActivityDrawerHeader.module.scss";

interface Props {
    historyRow: HistoryRow;
    activityTitle: string;
    date?: Date;
    dateClassName?: string;
    leadingContent?: ReactNode;
    titleSuffix?: ReactNode;
}

export const ActivityDrawerHeader = ({
    historyRow,
    activityTitle,
    date,
    dateClassName,
    leadingContent,
    titleSuffix,
}: Props) => {
    const visibleChannel = useVisibleHistoryChannel(historyRow.channel);
    const actorParams = {
        actorType: historyRow.actorType,
        actorName: historyRow.actorName,
        sourceType: historyRow.sourceType,
        fields: historyRow.fields,
        changeTypes: historyRow.changeTypes,
        system: historyRow.system,
        actorId: historyRow.actorId,
        memberName: historyRow.member?.user.preferredName,
    };
    const actorName = resolveActorDisplayName(actorParams);
    const channelLabel = resolveActorChannelLabel({
        ...actorParams,
        channel: visibleChannel,
    });

    const resolvedDate = date ?? historyRow.date;
    // Several applies can land on the same day, so the header carries the time of
    // day too, and a machine-readable timestamp for assistive tech.
    const hasUsableDate = isValid(resolvedDate);

    return (
        <div className={styles.root}>
            {leadingContent}
            {leadingContent && <span className={styles.divider}>·</span>}
            <ActorIcon
                actorType={historyRow.actorType}
                actorName={historyRow.actorName}
                sourceType={historyRow.sourceType}
                user={historyRow.member?.user}
                fields={historyRow.fields}
                changeTypes={historyRow.changeTypes}
                system={historyRow.system}
            />
            <ActorName
                name={actorName}
                channelLabel={channelLabel}
                className={styles.actorName}
            />
            <span className={styles.divider}>·</span>
            <h2 className={styles.title} title={activityTitle}>
                {activityTitle}
                {titleSuffix}
            </h2>
            {hasUsableDate && (
                <>
                    <span className={styles.divider}>·</span>
                    <time
                        dateTime={formatISO(resolvedDate)}
                        className={classNames(styles.date, dateClassName)}
                    >
                        {format(resolvedDate, fullDateFormatWithTime)}
                    </time>
                </>
            )}
        </div>
    );
};
